import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // Импортируем useNavigate
import CodeInput from "./CodeInput";
import { login, register, tgInit, verifyCode, updateUser, resendVerificationCode } from "../../../../../../http/UserAPI";
import { FaArrowLeft } from 'react-icons/fa'
import { observer } from "mobx-react-lite";
import { Context } from "../../../../../../index";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const RegistrationForm = observer(({ onStepChange, onClose }) => {
  const { user } = useContext(Context);
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const [reset, setReset] = useState(false);
  const [timer, setTimer] = useState(60); // Таймер для повторного запроса кода
  const [canResend, setCanResend] = useState(false); // Можно ли отправить код заново
  const [successMessage, setSuccessMessage] = useState(''); // ✅ Добавили setSuccessMessage

  const [info, setInfo] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    first_name: '',
    last_name: '',
    avatar: null
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    passwordConfirm: '',
    verificationCode: '',
    first_name: '',
    last_name: ''
  });
  const [backErrors, setBackErrors] = useState({//если запрос не удался
    error_step_0: '',
    error_step_1: '',
    error_step_2: '',
    error_step_3: '',
  });

  // Состояние для переключения отображения пароля
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  const resendTimerRef = useRef(null);

  const startResendTimer = () => {
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current); // Очищаем предыдущий таймер
    }

    setCanResend(false);
    setTimer(60);

    resendTimerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev === 1) {
          clearInterval(resendTimerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResendCode = async () => {
    if (!canResend) return;
    try {
      await resendVerificationCode(info.email);
      setSuccessMessage("✅ Код повторно отправлен на вашу почту!");
      startResendTimer();
    } catch (err) {
      setBackErrors({ error_resend: err.response?.data?.error || 'Ошибка при отправке нового кода.' });
    }
  };

  const handlePhotoUpload = (e) => {
    setInfo({ ...info, avatar: URL.createObjectURL(e.target.files[0]) });
  };

  useEffect(() => {
    onStepChange(step); // обновляем родительский компонент при изменении шага
  }, [step, onStepChange]);

  useEffect(() => {
    const prohibitedEmailDomain = new RegExp(/@((mail\.ru)|(bk\.ru))/)
    const errs = {
      email: ''
    }
    if (prohibitedEmailDomain.test(info.email)) {
      errs.email = 'Сайт временно не поддерживает отправку писем на домены mail.ru'
    }
    setErrors(errs)

  }, [info.email])


  const validate = () => {
    let valid = true;
    const newErrors = { ...errors };

    if (step === 1) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      newErrors.email = !info.email
        ? 'Пожалуйста, заполните поле E-mail.'
        : !emailRegex.test(info.email)
          ? 'Неверный формат E-mail.'
          : '';

      // Упрощённая валидация пароля: минимум 5 символов
      newErrors.password = !info.password
        ? 'Пожалуйста, заполните поле пароль.'
        : info.password.length < 5
          ? 'Пароль должен содержать минимум 5 символов.'
          : '';

      newErrors.passwordConfirm = !info.passwordConfirm
        ? 'Пожалуйста, подтвердите пароль.'
        : info.password !== info.passwordConfirm
          ? 'Пароли не совпадают.'
          : '';
    }

    if (step === 2) {
      newErrors.verificationCode = !info.verificationCode
        ? 'Пожалуйста, введите код подтверждения.'
        : '';
      setReset(true);
    }

    if (step === 3) {
      newErrors.first_name = !info.first_name ? 'Пожалуйста, введите ваше имя.' : '';
      newErrors.last_name = !info.last_name ? 'Пожалуйста, введите вашу фамилию.' : '';
    }

    if (Object.values(newErrors).some(error => error)) valid = false;

    setErrors(newErrors);
    return valid;
  };


  const handleLastStep = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    try {
      await verifyCode(info.email, info.verificationCode);
      setStep(3);
    } catch (err) {
      setBackErrors({ ...backErrors, error_step_2: err.response?.data?.error || 'Ошибка подтверждения кода.' });
    }
  };

  const [isPrivacyAgreed, setIsPrivacyAgreed] = useState(false);

  const handleNextStep2 = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    try {
      await register(info);
      setStep(2);
      startResendTimer();
    } catch (error) {
      setBackErrors({ ...backErrors, error_step_2: error.message });
    }
  };

  const handleNextStep3 = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    try {
      const response = await login(info.email, info.password);
      user.setIsAuth(true);
      user.setUser(response.body);
      await updateUser(info);
      if (typeof onClose === "function") {
        onClose();
      }
      navigate(user.userHomePath);
    } catch (error) {
      setBackErrors({ ...backErrors, error_step_3: error.message });
    }
  };

  return (
    <div className="auth__registration">
      {step === 1 && (
        <>
          <div className="auth__form-input">
            <label htmlFor="reg-email">E-mail</label>
            <input
              id="reg-email"
              type="email"
              value={info.email}
              onChange={(e) => setInfo({ ...info, email: e.target.value })}
            />
          </div>
          {errors.email && <p className="error-message text-red-500 my-4">{errors.email}</p>}
          <div className="auth__form-input password-container">
            <label htmlFor="reg-password">Пароль</label>
            <input
              id="reg-password"
              type={showPassword ? "text" : "password"}
              value={info.password}
              onChange={(e) => setInfo({ ...info, password: e.target.value })}
            />
            <button
              type="button"
              className="eye-button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>

          {errors.password && <p className="error-message text-red-500 my-4">{errors.password}</p>}
          <div className="auth__form-input password-container">
            <label htmlFor="reg-password-confirm">Ещё раз</label>
            <input
              id="reg-password-confirm"
              type={showPasswordConfirm ? "text" : "password"}
              value={info.passwordConfirm}
              onChange={(e) => setInfo({ ...info, passwordConfirm: e.target.value })}
            />
            <button
              type="button"
              className="eye-button"
              onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
            >
              {showPasswordConfirm ? <FaEyeSlash /> : <FaEye />}
            </button>
          </div>
          {errors.passwordConfirm && <p className="form__error-message">{errors.passwordConfirm}</p>}

          {/* Чекбокс с согласием с политикой конфиденциальности */}
          <label className="checkout-panel__agreement flex w-full mb-4">
            <input
              type="checkbox"
              checked={isPrivacyAgreed}
              onChange={(e) => setIsPrivacyAgreed(e.target.checked)}
            />
            <p>
              <span>Я соглашаюсь с </span>
              <a href="/files/privacy_policy.pdf" className="underline" target='_blank' download rel="noreferrer">политикой конфиденциальности</a>
            </p>
          </label>

          <button className="submit-button w-full" type="button" onClick={handleNextStep2} disabled={!isPrivacyAgreed}>
            Регистрация
          </button>
        </>
      )}

      {step === 2 && (
        <div className='auth__login'>

          <h2 className='confirmation__title'>Подтверждение</h2>
          <h3 className='confirmation__subtitle'>Введите 5-значный код, который пришел вам на e-mail</h3>
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: '25px',
            marginTop: '30px'
          }}>
            <CodeInput
              reset={reset}
              length={5}
              value={info.verificationCode}
              onComplete={(v) => setInfo({ ...info, verificationCode: v })}
            />
          </div>
          <button className="auth__login-recovery_btn" type="button"
            onClick={handleLastStep}>Подтвердить
          </button>
          <button
            className="resend-code-btn"
            type="button" // ✅ Добавлено, чтобы предотвратить перезагрузку
            onClick={handleResendCode}
            disabled={!canResend}
          >
            {canResend ? 'Отправить новый код' : `Отправить новый код (${timer}с)`}
          </button>

          {backErrors.error_resend && <p className="error-message">{backErrors.error_resend}</p>}
          {successMessage && <p className="success-message">{successMessage}</p>}
        </div>
      )}
      {step === 3 && (

        <div class="profile-card">

          <div class="profile-card__content">

            <div class="profile-card__avatar-wrapper">
              <div class="profile-card__avatar">
                {info.avatar ? (
                  <img
                    src={info.avatar}
                    alt="Uploaded"
                    class="profile-card__avatar-image"
                  />
                ) : null}
              </div>

              <label class="profile-card__upload-button">
                <input
                  type="file"
                  class="profile-card__upload-input"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                />
                Загрузить
              </label>
            </div>

            <div class="profile-card__input-wrapper">
              <label class="profile-card__label">Имя</label>
              <input
                type="text"
                placeholder="Введите ваше имя"
                value={info.first_name}
                onChange={(e) => setInfo({ ...info, first_name: e.target.value })}
                class="profile-card__input"
              />
              {errors.first_name && <p class="profile-card__error-message">{errors.first_name}</p>}
            </div>

            <div class="profile-card__input-wrapper">
              <label class="profile-card__label">Фамилия</label>
              <input
                type="text"
                placeholder="Введите вашу фамилию"
                value={info.last_name}
                onChange={(e) => setInfo({ ...info, last_name: e.target.value })}
                class="profile-card__input"
              />
              {errors.last_name && <p class="profile-card__error-message">{errors.last_name}</p>}
            </div>

            <button class="profile-card__save-button"
              type="button"
              onClick={handleNextStep3}
            >
              Сохранить
            </button>
            {backErrors.error_step_2 && <p class="profile-card__error-message">{backErrors.error_step_2}</p>}
          </div>
        </div>

      )}


    </div>
  );
})

export default RegistrationForm;
